import React, { useState } from 'react';
import { NotAssessed, SectionLabel } from '../shared/SurfaceParts';
import {
  ControlBridgeSpawnArtifactError,
  spawnControlBridgeArtifactItem,
} from '../../../services/controlBridgeSpawnArtifactService';
import styles from '../ControlBridge.module.css';

export interface QuoteRequestIntakeProps {
  workspaceSlug?: string;
  /** Required hosted company artifact for spawn (ISSUE-073 / report-issue CAR contract). */
  companyArtifactRef?: string;
  filterScope?: string;
  onSpawned?: (itemId: string) => void;
}

/**
 * Salesperson phone/walk-in quote request (`co-kfo-pia-quote-request-entry-form-ddd-20261001`).
 * Persists via POST /kryspace/workspace/{slug}/spawn-artifact-item (persistMode=patch).
 */
export const QuoteRequestIntake: React.FC<QuoteRequestIntakeProps> = ({
  workspaceSlug,
  companyArtifactRef,
  filterScope,
  onSpawned,
}) => {
  const [customerName, setCustomerName] = useState('');
  const [summary, setSummary] = useState('');
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canPersist = Boolean(workspaceSlug?.trim() && companyArtifactRef?.trim());

  return (
    <section aria-label="Quote request" data-testid="quote-request-intake">
      <SectionLabel>Start a quote request</SectionLabel>
      <form
        className={styles.quoteRequestForm}
        onSubmit={async (event) => {
          event.preventDefault();
          setError(null);
          setSubmittedId(null);
          if (!canPersist || !workspaceSlug || !companyArtifactRef) {
            setError('Workspace and company artifact are required to save a quote request.');
            return;
          }
          setBusy(true);
          try {
            const res = await spawnControlBridgeArtifactItem(workspaceSlug, {
              artifactTypeRef: 'pt-quote',
              name: summary.trim() || `Quote for ${customerName.trim()}`,
              description: summary.trim(),
              companyArtifactRef,
              filterScope,
              properties: [
                { key: 'customer_name', value: customerName.trim() },
                { key: 'request_summary', value: summary.trim() },
              ],
            });
            setSubmittedId(res.itemId);
            onSpawned?.(res.itemId);
            setCustomerName('');
            setSummary('');
          } catch (err: unknown) {
            const message =
              err instanceof ControlBridgeSpawnArtifactError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : 'Failed to save quote request';
            setError(message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className={styles.reportIssueLabel} htmlFor="cb-quote-customer">
          Customer
        </label>
        <input
          id="cb-quote-customer"
          className={styles.reportIssueInput}
          value={customerName}
          onChange={(event) => setCustomerName(event.target.value)}
          required
          autoComplete="off"
          maxLength={120}
          disabled={busy}
        />
        <label className={styles.reportIssueLabel} htmlFor="cb-quote-summary">
          Request
        </label>
        <textarea
          id="cb-quote-summary"
          className={styles.reportIssueTextarea}
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          required
          maxLength={500}
          rows={3}
          disabled={busy}
        />
        <button type="submit" className={styles.reportIssuePrimary} disabled={busy || !canPersist}>
          {busy ? 'Saving…' : 'Save request'}
        </button>
      </form>
      {error ? (
        <p className={styles.surfaceSubtitle} role="alert" data-testid="quote-request-error">
          {error}
        </p>
      ) : null}
      {submittedId ? (
        <p className={styles.surfaceSubtitle} data-testid="quote-request-saved">
          Saved quote request <code>{submittedId}</code> (persistMode=patch).
        </p>
      ) : !canPersist ? (
        <NotAssessed label="Quote request persist (missing workspace or company artifact)" />
      ) : (
        <p className={styles.surfaceSubtitle}>
          Saves a <code>pt-quote</code> item via Control Bridge spawn-artifact-item.
        </p>
      )}
    </section>
  );
};
